// 前台外观偏好的取值集合 — 站长在后台设默认, 访客在 /settings 覆盖 (存 localStorage)。
// 两层模型: localStorage 有值 → 访客覆盖优先; 无值 → 站长默认 (SiteConfig); 都没有 → 内置 fallback。

export const BG_MODES = [
  'shader',
  'shader-dawn',
  'shader-night',
  'shader-auto',
  'classic',
] as const;
export type BgMode = (typeof BG_MODES)[number];

export const READING_FONTS = ['small', 'normal', 'large'] as const;
export type ReadingFont = (typeof READING_FONTS)[number];

export const READING_LINES = ['compact', 'normal', 'loose'] as const;
export type ReadingLineHeight = (typeof READING_LINES)[number];

export const READING_WIDTHS = ['normal', 'wide'] as const;
export type ReadingWidth = (typeof READING_WIDTHS)[number];

export const TEXT_SELECT_MODES = ['on', 'off'] as const;
export type TextSelectMode = (typeof TEXT_SELECT_MODES)[number];

// 强调色套餐 — 只作用链接/chip/高亮/选区等点缀色 (不碰正文背景/文字)。
// 受限枚举而非自由 hex: 守 Aurora 暖色弧 + 保证在半透明玻璃卡上对比度够。coral = 现有品牌默认。
export const ACCENT_PRESETS = ['coral', 'rose', 'amber', 'magenta'] as const;
export type AccentPreset = (typeof ACCENT_PRESETS)[number];

export interface AccentColors {
  accent: string;
  accentSoft: string;
  magenta: string;
}

// 每套三元组对应 global.css 的 --aurora-accent / --aurora-accent-soft / --aurora-magenta。
// 候选都在暖色 (珊瑚→玫瑰→琥珀→品红) 弧内, 都验过在玻璃卡上链接色可读。
export const ACCENT_PRESET_COLORS: Record<AccentPreset, AccentColors> = {
  coral: { accent: '#ff7e5f', accentSoft: '#feb47b', magenta: '#e85d75' },
  rose: { accent: '#ff6f91', accentSoft: '#ffb0a8', magenta: '#e85d8f' },
  amber: { accent: '#ff9f5a', accentSoft: '#ffd07a', magenta: '#f07d52' },
  magenta: { accent: '#f072a8', accentSoft: '#f5a8c8', magenta: '#d65a92' },
};

// 密度套餐 — 一个旋钮换全站气质 (玻璃卡圆角 + 背景模糊)。纯几何不碰颜色, 零可读性风险。
// cozy = 现有默认 (radius 20 / blur 24)。站长设, 全站生效 (注入 documentElement)。
export const DENSITY_PRESETS = ['compact', 'cozy', 'airy'] as const;
export type DensityPreset = (typeof DENSITY_PRESETS)[number];

export interface DensityTokens {
  glassRadius: number; // px → --glass-radius
  glassBlur: number; // px → --glass-blur
}
export const DENSITY_PRESET_TOKENS: Record<DensityPreset, DensityTokens> = {
  compact: { glassRadius: 12, glassBlur: 16 }, // 利落紧凑
  cozy: { glassRadius: 20, glassBlur: 24 }, // 默认 (= global.css :root 默认值)
  airy: { glassRadius: 30, glassBlur: 30 }, // 松弛柔和
};

// 整站视觉主题皮肤 — aurora (暖色极光, 默认) / polar (赛博霓虹)。
// 两层模型 (同 BG_MODES): 站长设默认 (SiteConfig.defaultThemeSkin), 访客在 /settings 可覆盖 (localStorage)。
// 应用机制: documentElement.dataset.theme → global.css 的 :root[data-theme="polar"] 覆盖同名 --aurora-* 变量块。
export const THEME_SKINS = ['aurora', 'polar'] as const;
export type ThemeSkin = (typeof THEME_SKINS)[number];

// 前台功能开关 — 站长按站关掉某些功能/彩蛋 (做减法的归属感)。默认全开 = 现状零变化。
// 关掉只隐藏前台入口 (后端 endpoint 保留, 数据回灌等仍可用)。
export interface FeatureFlags {
  likes: boolean; // 文章点赞
  readingResume: boolean; // 继续阅读卡片 + 进度记录
  search: boolean; // 全站搜索 (含「问我的博客」入口)
  rag: boolean; // RAG 问答 (search 内的 ask 态; search 关则一并不可达)
  wpmGame: boolean; // 打字闯关 /wpm
  konami: boolean; // Konami 彩蛋
}
export const FEATURE_KEYS = [
  'likes',
  'readingResume',
  'search',
  'rag',
  'wpmGame',
  'konami',
] as const;
export const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
  likes: true,
  readingResume: true,
  search: true,
  rag: true,
  wpmGame: true,
  konami: true,
};

// 首页内容块编排 — 站长在后台排序/显隐这 5 个内容块 (品牌锚定块 status/hero/bio/resume 锁死不参与)。
// 站长设全站生效 (首页形态是站点身份选择, 不做访客覆盖)。
export const HOME_MODULE_KEYS = ['cards', 'notes', 'work', 'popular', 'counts'] as const;
export type HomeModuleKey = (typeof HOME_MODULE_KEYS)[number];

export interface HomeModuleConfig {
  key: HomeModuleKey;
  enabled: boolean;
  order: number;
}
// 默认顺序 = 当前线上视觉顺序, 全开
export const DEFAULT_HOME_MODULES: HomeModuleConfig[] = [
  { key: 'cards', enabled: true, order: 0 },
  { key: 'notes', enabled: true, order: 1 },
  { key: 'work', enabled: true, order: 2 },
  { key: 'popular', enabled: true, order: 3 },
  { key: 'counts', enabled: true, order: 4 },
];
